/*
 * Текст документа PDF: поиск цитаты и подготовка цитаты из выделения мышью.
 *
 * Модуль намеренно без pdfjs: сюда приходят уже готовые куски текста в долях
 * страницы, а сама библиотека грузится динамически в DocPages. Так поиск
 * цитаты проверяется обычным тестом, без браузера и без воркера.
 *
 * Документ здесь — полотно (`Canvas`), то есть слепки страниц по порядку, а
 * не одна страница. Абзац договора запросто начинается на одной странице и
 * кончается на другой, и пока цитата искалась в пределах листа, такой абзац
 * нельзя было выделить целиком и нельзя было завести по нему одно замечание
 * (задача Ильи 21.09.2026). Разрыв страницы склеивается переводом строки: в
 * docx на этом месте стоит обычный пробел между словами, а нормализация
 * сводит к пробелу и то и другое.
 *
 * Пробелы нормализуются так же, как на сервере (server/src/contracts/anchors.ts):
 * один и тот же кусок текста в документе легко набран через двойной или
 * неразрывный пробел, а человек выделяет его глазами.
 */

/** Кусок текста страницы. Координаты — доли страницы (0..1), y от верха. */
export type TextItem = { str: string; x: number; y: number; w: number; h: number };

export type Rect = { x: number; y: number; w: number; h: number };

/** Полотно документа: слепок текста каждой страницы по порядку. */
export type Canvas = TextItem[][];

/** Кусок подсветки: прямоугольник на своей странице полотна. */
export type Part = { page: number; rect: Rect };

/** Длиннее цитата не нужна: в пузырь на полях она всё равно не влезет. */
const MAX_QUOTE = 200;

/**
 * Колонтитул длиннее этого не бывает, а строка договора — бывает. Порог
 * страхует распознавание: полоса текста во всю строку колонтитулом не
 * объявляется, даже если совпали высота и очертание.
 */
const MAX_RUNNING = 60;

/** Насколько колонтитул может гулять по высоте от страницы к странице. */
const RUNNING_Y = 0.01;

const isSpace = (c: string) => /\s/.test(c);

/** Нормализованный текст и карта «символ нормализованного → место в исходном». */
function normalize(text: string): { norm: string; map: number[] } {
  let norm = '';
  const map: number[] = [];
  let space = false;
  for (let i = 0; i < text.length; i++) {
    if (isSpace(text[i])) {
      space = norm.length > 0;
      continue;
    }
    if (space) {
      norm += ' ';
      map.push(i);
      space = false;
    }
    norm += text[i];
    map.push(i);
  }
  return { norm, map };
}

/** Одна строка или нет: куски строки стоят на одной высоте, перенос уводит вниз
 *  больше чем на половину роста. */
function sameLine(a: TextItem, b: TextItem): boolean {
  return Math.abs(a.y + a.h / 2 - (b.y + b.h / 2)) <= Math.max(a.h, b.h) / 2;
}

function bounds(line: TextItem[]): Rect {
  const x = Math.min(...line.map((it) => it.x));
  const y = Math.min(...line.map((it) => it.y));
  const right = Math.max(...line.map((it) => it.x + it.w));
  const bottom = Math.max(...line.map((it) => it.y + it.h));
  return { x, y, w: right - x, h: bottom - y };
}

/** Где в полотне лежит символ склейки: страница и номер куска на ней. */
type Ref = { page: number; i: number };

/**
 * Полотно одной строкой. Страницы склеиваются переводом строки: последний
 * кусок страницы не всегда несёт признак конца строки, а без разделителя
 * последнее слово страницы слиплось бы с первым словом следующей и цитата
 * через разрыв не нашлась бы ни здесь, ни в слепке docx на сервере.
 */
function stream(canvas: Canvas): { text: string; owner: Ref[] } {
  let text = '';
  const owner: Ref[] = [];
  for (let p = 0; p < canvas.length; p++) {
    const items = canvas[p];
    for (let i = 0; i < items.length; i++) {
      for (let j = 0; j < items[i].str.length; j++) {
        text += items[i].str[j];
        owner.push({ page: p, i });
      }
    }
    if (p < canvas.length - 1 && items.length) {
      text += '\n';
      owner.push({ page: p, i: items.length - 1 });
    }
  }
  return { text, owner };
}

/**
 * Склейка полотна считается один раз на полотно. Замечаний в договоре бывает
 * несколько десятков, а укорачивание цитаты перебирает слова с конца: без
 * памяти на склейку строка документа пересобиралась бы сотни раз подряд.
 * Ключ — сам массив страниц: новый разбор даёт новый массив, и память
 * обновляется сама.
 */
const joined = new WeakMap<Canvas, { owner: Ref[]; norm: string; map: number[] }>();

function indexOf(canvas: Canvas): { owner: Ref[]; norm: string; map: number[] } {
  const known = joined.get(canvas);
  if (known) return known;
  const { text, owner } = stream(canvas);
  const { norm, map } = normalize(text);
  const made = { owner, norm, map };
  joined.set(canvas, made);
  return made;
}

/**
 * Прямоугольники цитаты на полотне: по одному на строку, и строка знает свою
 * страницу. Цитата через перенос даёт два прямоугольника, цитата через разрыв
 * страницы — прямоугольники на двух страницах: подсветка ложится по строкам, а
 * не одним кубом поперёк абзаца. Затронутые куски берутся целиком: доли
 * символа внутри куска из слепка не видно, а лишние миллиметры подсветки
 * читателю не мешают.
 */
export function locateAll(canvas: Canvas, quote: string): Part[] {
  const doc = indexOf(canvas);
  const needle = normalize(quote).norm.trim();
  if (!needle) return [];
  const at = doc.norm.indexOf(needle);
  if (at < 0) return [];

  const from = doc.owner[doc.map[at]];
  const to = doc.owner[doc.map[at + needle.length - 1]];

  const out: Part[] = [];
  let line: TextItem[] = [];
  let linePage = from.page;
  const flush = () => {
    if (line.length) out.push({ page: linePage, rect: bounds(line) });
    line = [];
  };
  for (let p = from.page; p <= to.page; p++) {
    const items = canvas[p];
    const first = p === from.page ? from.i : 0;
    const last = p === to.page ? to.i : items.length - 1;
    for (let i = first; i <= last; i++) {
      const it = items[i];
      if (!it.str) continue;
      if (line.length && (p !== linePage || !sameLine(line[line.length - 1], it))) flush();
      if (!line.length) linePage = p;
      line.push(it);
    }
  }
  flush();
  return out;
}

/**
 * Место уже написанного замечания.
 *
 * Сперва в теле, затем во всём, что напечатано на страницах. Второй заход —
 * ради замечаний, написанных до перехода на полотно: колонтитулы тогда стояли
 * в слое текста, и гость, выделивший текст до самого низа листа, мог унести в
 * цитату номер страницы. Сервер такую цитату принимал: пункт ему подсказывает
 * клиент, а у гостя разбора версии нет вовсе и замечание уходит без пункта.
 * Искать её теперь только в теле значило бы, что старая ветка молча пропала с
 * поля. Место при этом остаётся своим: ищется та же цитата по тому, что
 * действительно напечатано.
 *
 * Новым цитатам второй заход не нужен и не дан (`quoteOnCanvas` работает по
 * телу): цитата с колонтитулом внутри не найдётся в слепке docx, и пункта у
 * такого замечания не будет.
 */
export function locateQuote(body: Canvas, printed: Canvas, quote: string): Part[] {
  const parts = locateAll(body, quote);
  return parts.length ? parts : locateAll(printed, quote);
}

/** Обрезка по границе слова: половина слова в цитате и выглядит обрывком, и
 *  хуже находится в следующей версии договора. */
function clip(quote: string): string {
  if (quote.length <= MAX_QUOTE) return quote;
  const cut = quote.slice(0, MAX_QUOTE);
  const space = cut.lastIndexOf(' ');
  return (space > 0 ? cut.slice(0, space) : cut).trim();
}

/**
 * Цитата из выделения мышью. Мышь берёт текст с разметки, а не со слепка
 * полотна: в выделение попадает и то, чего в слепке нет. Поэтому цитата,
 * которая на полотне не находится, укорачивается с конца по словам, пока не
 * найдётся, — иначе замечание сразу ложится «не на место».
 */
export function quoteOnCanvas(canvas: Canvas, selectedText: string): string {
  const quote = clip(normalize(selectedText).norm.trim());
  if (!quote || locateAll(canvas, quote).length) return quote;

  const words = quote.split(' ');
  for (let n = words.length - 1; n > 0; n--) {
    const shorter = words.slice(0, n).join(' ');
    if (locateAll(canvas, shorter).length) return shorter;
  }
  return quote;
}

/** Строки страницы сверху вниз: номера кусков, стоящих на одной высоте. */
function rows(items: TextItem[]): number[][] {
  const order = items
    .map((_, i) => i)
    .filter((i) => items[i].str.trim())
    .sort((a, b) => items[a].y - items[b].y);
  const out: number[][] = [];
  for (const i of order) {
    const line = out[out.length - 1];
    if (line && sameLine(items[line[line.length - 1]], items[i])) line.push(i);
    else out.push([i]);
  }
  return out;
}

/** Очертание строки: цифры в один знак — номер страницы меняется, колонтитул нет. */
function shapeOf(line: number[], items: TextItem[]): string {
  return normalize(line.map((i) => items[i].str).join(' '))
    .norm.trim()
    .replace(/\d+/g, '#');
}

/**
 * Полотно без колонтитулов.
 *
 * Колонтитулы в слепок docx не попадают вовсе: сервер разбирает `document.xml`,
 * а верхний и нижний живут в `header*.xml` и `footer*.xml`. Значит замечание к
 * ним нельзя было завести и раньше — сервер не нашёл бы пункта. Зато пока они
 * стояли в слое текста, номер страницы попадал в середину цитаты через разрыв
 * («…конца страницы 3 начало…»), и сервер отказывал уже законному замечанию.
 * Поэтому они выбрасываются из слоя текста целиком: на растре листа они
 * по-прежнему видны, просто не выделяются.
 *
 * Признаётся колонтитулом только верхняя или нижняя строка, которая
 * повторяется на других страницах на той же высоте с тем же очертанием и
 * коротка. Не узнали — цитата через такой разрыв не соберётся и замечание не
 * уйдёт с внятным отказом; это лучше, чем замечание, севшее не на тот текст.
 */
export function bodyCanvas(canvas: Canvas): Canvas {
  if (canvas.length < 2) return canvas;
  const lines = canvas.map((items) => rows(items));
  const drop = canvas.map(() => new Set<number>());

  for (const side of [0, 1]) {
    const cand = canvas.map((items, p) => {
      // Страница в одну-две строки — это не колонтитул и не текст, а редкий
      // хвост договора: выбрасывать из неё нечего.
      if (lines[p].length < 3) return null;
      const line = side === 0 ? lines[p][0] : lines[p][lines[p].length - 1];
      const shape = shapeOf(line, items);
      if (!shape || shape.length > MAX_RUNNING) return null;
      return { line, shape, y: items[line[0]].y };
    });
    for (let p = 0; p < cand.length; p++) {
      const c = cand[p];
      if (!c) continue;
      let same = 0;
      for (let q = 0; q < cand.length; q++) {
        const d = q === p ? null : cand[q];
        if (d && d.shape === c.shape && Math.abs(d.y - c.y) <= RUNNING_Y) same++;
      }
      if (same >= 1 && (same + 1) * 2 >= canvas.length) for (const i of c.line) drop[p].add(i);
    }
  }

  return canvas.map((items, p) => (drop[p].size ? items.filter((_, i) => !drop[p].has(i)) : items));
}
